// LeaseAuditNew Types

export type AuditType = 'UPLOADED' | 'INTEGRATED'

/**
 * Steps in the lease audit workflow
 */
export type LeaseAuditStep =
    | 'CREATE'
    | 'RENT_ROLL_PROCESSING'
    | 'ATTRIBUTE_SELECTION'
    | 'DOCUMENT_UPLOAD'
    | 'DOCUMENT_PARSING'
    | 'DOCUMENT_REVIEW'
    | 'DATA_EXTRACTION'
    | 'AUDITING'

/**
 * All steps in order for the stepper display
 */
export const LEASE_AUDIT_STEPS: LeaseAuditStep[] = [
    'RENT_ROLL_PROCESSING',
    'DOCUMENT_UPLOAD',
    'DOCUMENT_PARSING',
    'DOCUMENT_REVIEW',
    'ATTRIBUTE_SELECTION',
    'DATA_EXTRACTION',
    'AUDITING',
]

/**
 * Human-readable labels for each step
 */
export const STEP_LABELS: Record<LeaseAuditStep, string> = {
    CREATE: 'Create',
    RENT_ROLL_PROCESSING: 'Rent Roll Processing',
    ATTRIBUTE_SELECTION: 'Attribute Selection',
    DOCUMENT_UPLOAD: 'Document Upload',
    DOCUMENT_PARSING: 'Document Parsing',
    DOCUMENT_REVIEW: 'Review Resident Terms',
    DATA_EXTRACTION: 'Data Extraction',
    AUDITING: 'Auditing',
}

/**
 * Map steps to their corresponding tab index
 * Tab order: Rent Roll, Lease Documents, Resident Terms, Audit, Analysis, Reports, Attributes
 */
export const STEP_TO_TAB: Record<LeaseAuditStep, number> = {
    CREATE: 0, // Rent Roll Tab
    RENT_ROLL_PROCESSING: 0,
    DOCUMENT_UPLOAD: 1, // Lease Documents Tab
    DOCUMENT_PARSING: 1,
    DOCUMENT_REVIEW: 2, // Resident Terms Tab
    ATTRIBUTE_SELECTION: 6, // Attributes Tab
    DATA_EXTRACTION: 1, // Lease Documents Tab
    AUDITING: 3, // Audit Tab
}

/**
 * Step order for progression comparison
 * Higher numbers indicate later steps in the workflow
 */
export const STEP_ORDER: Record<LeaseAuditStep, number> = {
    CREATE: 0,
    RENT_ROLL_PROCESSING: 1,
    DOCUMENT_UPLOAD: 2,
    DOCUMENT_PARSING: 3,
    DOCUMENT_REVIEW: 4,
    ATTRIBUTE_SELECTION: 5,
    DATA_EXTRACTION: 6,
    AUDITING: 7,
}

/**
 * Helper function to check if a step has been reached in the workflow
 * @param currentStep - The current step in the workflow
 * @param requiredStep - The step that needs to be reached
 * @returns true if currentStep is at or past the requiredStep
 */
export const isStepReached = (
    currentStep: LeaseAuditStep,
    requiredStep: LeaseAuditStep,
): boolean => {
    return STEP_ORDER[currentStep] >= STEP_ORDER[requiredStep]
}

/**
 * Attribute datatype options
 */
export type AttributeDataType =
    | 'CURRENCY'
    | 'DATE'
    | 'TEXT'
    | 'NUMBER'
    | 'BOOLEAN'

/**
 * Attribute configuration for rent roll columns
 */
export interface Attribute {
    id: number
    name: string
    should_display: boolean
    should_extract: boolean
    prompt: string
    data_type: AttributeDataType | null
    display_order: number
}

/**
 * Time period categorization for resident terms
 */
export type TimePeriod = 'past' | 'current' | 'future'

/**
 * Term status from backend (uppercase version)
 */
export type TermStatus = 'PAST' | 'CURRENT' | 'FUTURE'

/**
 * Term status filter options for lease audit
 */
export type TermStatusFilter =
    | 'ALL_PERIODS'
    | 'CURRENT_ONLY'
    | 'CURRENT_AND_FUTURE'

/**
 * Human-readable labels for term status filters
 */
export const TERM_STATUS_FILTER_LABELS: Record<TermStatusFilter, string> = {
    ALL_PERIODS: 'All Periods',
    CURRENT_ONLY: 'Current Only',
    CURRENT_AND_FUTURE: 'Current + Future',
}

/**
 * Document model for lease documents
 */
export interface LeaseDocument {
    id: number
    file: string // S3 URL to the document
    page_count?: number
    title: string
    resident: string
    resident_term: number | null
    lease_start: string
    lease_end: string
    is_selected: boolean
    file_name: string
    created_date: string
    processing_status?: DocumentProcessingStatus | null
    modified_date: string
}

export type DocumentProcessingStatusFilter =
    | 'SELECTION_COMPLETE'
    | 'EXTRACTION_FAILED'

export type DocumentProcessingStatus =
    | 'CREATED'
    | 'PARSING_PENDING'
    | 'PARSING_IN_PROGRESS'
    | 'PARSED'
    | 'PARSING_FAILED'
    | 'SELECTION_PENDING'
    | 'SELECTION_COMPLETE'
    | 'SELECTION_FAILED'
    | 'EXTRACTION_PENDING'
    | 'EXTRACTION_IN_PROGRESS'
    | 'EXTRACTED'
    | 'EXTRACTION_FAILED'

/**
 * Completion status for resident terms
 */
export type CompletionStatus =
    | 'complete'
    | 'needs_review'
    | 'reviewed'
    | 'missing'

/**
 * ResidentTerm model representing a lease term for a resident
 */
export interface ResidentTerm {
    id: number
    resident: string
    lease_start: string
    lease_end: string
    bldg_unit: string
    term_status: TermStatus
    paired_documents?: LeaseDocument[]
    paired_document_count?: number
    document_count?: number
    has_selected_document?: boolean
    reviewed?: boolean
    completion_status?: CompletionStatus
}

/**
 * Pagination response wrapper
 */
export interface PaginatedResponse<T> {
    count: number
    next: string | null
    previous: string | null
    results: T[]
}

/**
 * Rent roll row data
 */
export interface RentRollRow {
    id: number
    resident: string
    term_status: string
    values: Record<string, RentRollCellValue>
}

/**
 * Cell value in rent roll
 */
export interface RentRollCellValue {
    value: string | number | null
    coordinates?: string
    document_id?: number
}

/**
 * Audit cell containing both rent roll and document values
 */
export interface AuditCellValue {
    rent_roll_value: string | number | null
    document_value: string | number | null
    has_variance: boolean
}

/**
 * Analysis summary for currency attributes
 */
export interface AnalysisSummary {
    attribute_name: string
    rent_roll_total: number
    document_total: number
    variance: number
    variance_percentage: number
}

/**
 * Extended LeaseAudit type with current_step
 */
export interface LeaseAuditDetail {
    id: number
    name: string
    current_step: LeaseAuditStep
    latest_ready_step: LeaseAuditStep
    status: string
    created_date: string
    created_by: {
        id: number
        name: string
    }
    document_count: number
    page_count: number
    resident_term_count: number
    attributes: Attribute[]
    rent_roll_processing_task_id?: string | null
    parsing_task_id?: string | null
    extraction_task_id?: string | null
    data_extraction_task_id?: string | null
    term_status_filter: TermStatusFilter
    audit_type: AuditType
}

/**
 * Response from backend when requesting a presigned S3 URL
 */
export interface PresignedUrlResponse {
    presigned_url: string
    s3_key: string
    upload_id: number
    expires_in: number
}

/**
 * Upload progress tracking information
 */
export interface UploadProgress {
    percentage: number
    bytesUploaded: number
    totalBytes: number
    speed: number // bytes per second
    eta: number // seconds remaining
    startTime: number
}

/**
 * Request to notify backend that upload is complete
 */
export interface UploadCompleteRequest {
    s3_key: string
    upload_id: number
}

/**
 * Status of the extraction job
 */
export type UploadJobStatus = 'pending' | 'processing' | 'completed' | 'failed'

/**
 * Status of document upload records
 */
export type UploadStatus =
    | 'PENDING'
    | 'UPLOADED'
    | 'PROCESSING'
    | 'COMPLETED'
    | 'FAILED'

/**
 * Document upload record for tracking upload history
 */
export interface DocumentUploadRecord {
    id: number
    file: string
    original_filename: string
    file_size: number
    status: UploadStatus
    uploaded_by: {
        id: number
        name: string
    }
    error_message?: string | null
    multipart_upload_id?: string | null
    created_date: string
    modified_date: string
}

/**
 * Response from backend after upload completion
 */
export interface UploadCompleteResponse {
    task_id: string
    upload_id: number
    message?: string
    status?: UploadJobStatus
}

/**
 * Response from backend when initiating multipart upload
 */
export interface MultipartInitiateResponse {
    upload_id: number
    s3_key: string
    multipart_upload_id: string
    expires_in: number
}

/**
 * Request to get presigned URL for a specific part
 */
export interface PartPresignedUrlRequest {
    upload_id: number
    multipart_upload_id: string
    part_number: number
}

/**
 * Response containing presigned URL for a specific part
 */
export interface PartPresignedUrlResponse {
    presigned_url: string
    part_number: number
}

/**
 * Metadata for a completed multipart upload part
 */
export interface MultipartPart {
    part_number: number
    etag: string
}

/**
 * Request to complete a multipart upload
 */
export interface CompleteMultipartRequest {
    upload_id: number
    multipart_upload_id: string
    parts: MultipartPart[]
}

export type TaskType =
    | 'UPLOADED_RENT_ROLL_PROCESSING'
    | 'INTEGRATED_RENT_ROLL_PROCESSING'
    | 'UPLOADED_DOCUMENT_PARSING'
    | 'INTEGRATED_DOCUMENT_PARSING'
    | 'DATA_EXTRACTION'
export type TaskStatus = 'PENDING' | 'RUNNING' | 'SUCCESS' | 'FAILED'
export type SubTaskType =
    | 'PAST_TERMS_PROCESSING'
    | 'CURRENT_TERMS_PROCESSING'
    | 'FUTURE_TERMS_PROCESSING'
    | 'DOWNLOADING_DOCUMENTS'
    | 'PARSING_DOCUMENTS'
    | 'SELECTING_DOCUMENTS'
    | 'RETRIEVING_DOCUMENTS'
    | 'DATA_EXTRACTION'

export interface SubTaskResults {
    sub_task_status: TaskStatus
    sub_task_message: string | null
    sub_task_error: string | null
    sub_task_started_at: string | null
    sub_task_completed_at: string | null
    currently_on_record: number | null
    total_records: number | null
    elapsed_seconds: number | null
    estimated_remaining_seconds: number | null
}

export interface SubTask {
    sub_task_type: SubTaskType
    results: SubTaskResults
}

// Task Progress Response
export interface TaskProgressResponse {
    task_type: TaskType
    task_status: TaskStatus
    task_error?: string | null
    parse_config_status?: RentRollParseConfigStatus | null
    sub_tasks: SubTask[]
}

export type RentRollParseConfigStatus =
    | 'PENDING'
    | 'DETECTED'
    | 'FAILED'
    | 'MANUAL'

export interface RentRollSummaryResponse {
    filename: string | null
    parse_config_status: RentRollParseConfigStatus
    style: RentRollParseStyle | null
    current_resident_count: number
    future_resident_count: number
    confidence: number | null
    notes: string | null
}

export interface RentRollReportIssueResponse {
    ticket_id: number
    message: string
}

/**
 * Status of audit value comparison between rent roll and document
 */
export type AuditValueStatus =
    | 'MATCHED'
    | 'NOT_MATCHED'
    | 'MISSING_RENT_ROLL'
    | 'MISSING_DOCUMENT'

/**
 * Attribute definition for audit results
 */
export interface AuditAttribute {
    id: number
    name: string
    display_order: number
    should_extract: boolean
    data_type: AttributeDataType | null
}

/**
 * Single attribute value from audit comparison
 */
export interface AuditAttributeValue {
    attribute_id: number
    rent_roll_value: string | null
    document_value: string | null
    rent_roll_coordinates: string | null
    document_page_number: number | null
    document_file?: string | null
    conflict_value?: string | null
    conflict_document_file?: string | null
    conflict_page_number?: number | null
    review_note?: string | null
    review_note_author_name?: string | null
    review_note_at?: string | null
    values_match: boolean
}

/**
 * Resident term with audit results
 */
export interface AuditResidentTerm {
    id: number
    term_status: TermStatus
    selected_document_file: string
    resident?: string | null
    lease_start?: string | null
    lease_end?: string | null
    bldg_unit?: string | null
    attribute_values: AuditAttributeValue[]
}

/**
 * Complete audit results response
 */
export interface AuditResults {
    attributes: AuditAttribute[]
    rent_roll_file: string
    resident_terms: AuditResidentTerm[]
}

export type AuditAttributeMismatchResponse =
    | AuditResidentTerm[]
    | PaginatedResponse<AuditResidentTerm>

export interface AttributeSummary {
    attribute_id?: number
    id?: number
    name: string
    display_order: number
    should_extract: boolean
    data_type: AttributeDataType | null
    match_count: number
    mismatch_count: number
}

/**
 * Rent roll spreadsheet parse style detected by AI
 */
export type RentRollParseStyle = 'ledger' | 'flat'

/**
 * AI-detected or admin-edited rent roll parse configuration
 */
export interface RentRollParseConfig {
    style: RentRollParseStyle
    current_header_row: number
    current_last_data_row: number
    future_header_row: number | null
    future_last_data_row: number | null
    resident_start_rows: number[] | null
    resident_boundary_rule: string | null
    resident_boundary_column: string | null
    confidence: number | null
    notes: string | null
}

export interface RentRollGridCell {
    column: number
    coordinate?: string
    value: string | number | null
}

export interface RentRollGridRow {
    row_number: number
    cells: RentRollGridCell[]
}

export interface RentRollGrid {
    rows: RentRollGridRow[]
}

export interface RentRollResidentBand {
    start_row: number
    end_row: number
}

export interface RentRollHighlights {
    current_header_row: number
    current_last_data_row: number
    future_header_row: number | null
    future_last_data_row: number | null
    current_resident_bands?: RentRollResidentBand[]
    future_resident_bands?: RentRollResidentBand[]
    resident_count?: number
    future_resident_count?: number
}

export interface RentRollPreviewResponse {
    grid: RentRollGrid
    parse_config: RentRollParseConfig
    highlights: RentRollHighlights
}

export type RentRollPreviewEditMode =
    | 'current_header'
    | 'current_last_data'
    | 'future_header'
    | 'future_last_data'
    | 'resident_start'

export const RENT_ROLL_STYLE_LABELS: Record<RentRollParseStyle, string> = {
    flat: 'One row per resident',
    ledger: 'Multiple rows per resident',
}
